import React, { useState, useEffect } from 'react';
import { Button, Table, Tag, Space, message, Card, Typography, Row, Col, Statistic } from 'antd';
import {
    UserOutlined,
    MailOutlined,
    PlusOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { UserFrom1C, InviteToken, UserRole } from '../types/admin.types';
import adminApi from '../services/adminApi';
import { InviteUsersModal } from '../components/users/InviteUsersModal';

const { Title, Text } = Typography;

export const UsersManagementPage: React.FC = () => {
    const [usersFrom1C, setUsersFrom1C] = useState<UserFrom1C[]>([]);
    const [inviteTokens, setInviteTokens] = useState<InviteToken[]>([]);
    const [loading, setLoading] = useState(false);
    const [inviteModalVisible, setInviteModalVisible] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<UserFrom1C[]>([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [usersResponse, tokensResponse] = await Promise.all([
                adminApi.getUsersFrom1C(),
                adminApi.getInviteTokens()
            ]);

            if (usersResponse.data.success) {
                setUsersFrom1C(usersResponse.data.data || []);
            }

            if (tokensResponse.data.success) {
                setInviteTokens(tokensResponse.data.data || []);
            }
        } catch (error) {
            console.error('Ошибка загрузки данных:', error);
            message.error('Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    const handleInviteUsers = (users: UserFrom1C[]) => {
        console.log('handleInviteUsers called with:', users);
        setSelectedUsers(users);
        setInviteModalVisible(true);
        console.log('Modal should be visible now');
    };

    const handleInviteSuccess = () => {
        setInviteModalVisible(false);
        setSelectedUsers([]);
        loadData();
        message.success('Приглашения отправлены успешно');
    };

    const handleRevokeToken = async (tokenId: string) => {
        try {
            await adminApi.revokeInviteToken(tokenId);
            message.success('Токен отозван');
            loadData();
        } catch (error) {
            console.error('Ошибка отзыва токена:', error);
            message.error('Ошибка отзыва токена');
        }
    };

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'MODERATOR': return 'blue';
            case 'SIGNATORY': return 'green';
            default: return 'default';
        }
    };

    const getRoleLabel = (role: string) => {
        switch (role) {
            case 'MODERATOR': return 'Модератор';
            case 'SIGNATORY': return 'Подписант';
            default: return role;
        }
    };

    const getTokenStatus = (token: InviteToken) => {
        if (token.isUsed) {
            return { color: 'green', text: 'Использован', icon: <CheckCircleOutlined /> };
        }
        if (new Date(token.expiresAt) < new Date()) {
            return { color: 'red', text: 'Истек', icon: <ExclamationCircleOutlined /> };
        }
        return { color: 'blue', text: 'Активен', icon: <ClockCircleOutlined /> };
    };

    const usersColumns = [
        {
            title: 'ФИО',
            dataIndex: 'fullName',
            key: 'fullName',
            render: (text: string) => <Text strong>{text}</Text>
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (text: string) => (
                <Space>
                    <MailOutlined />
                    {text}
                </Space>
            )
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            render: (role: string) => (
                <Tag color={getRoleColor(role)}>
                    {getRoleLabel(role)}
                </Tag>
            )
        },
        {
            title: 'Отдел',
            dataIndex: 'department',
            key: 'department',
            render: (text: string) => text || '-'
        },
        {
            title: 'Должность',
            dataIndex: 'position',
            key: 'position',
            render: (text: string) => text || '-'
        },
        {
            title: 'Статус',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Активен' : 'Неактивен'}
                </Tag>
            )
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: UserFrom1C) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={() => handleInviteUsers([record])}
                    disabled={!record.isActive}
                >
                    Пригласить
                </Button>
            )
        }
    ];

    const tokensColumns = [
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            render: (role: UserRole) => (
                <Tag color={getRoleColor(role)}>
                    {getRoleLabel(role)}
                </Tag>
            )
        },
        {
            title: 'Статус',
            key: 'status',
            render: (_: any, record: InviteToken) => {
                const status = getTokenStatus(record);
                return (
                    <Tag color={status.color} icon={status.icon}>
                        {status.text}
                    </Tag>
                );
            }
        },
        {
            title: 'Создан',
            dataIndex: 'expiresAt',
            key: 'createdAt',
            render: (expiresAt: string) => {
                const created = new Date(new Date(expiresAt).getTime() - 7 * 24 * 60 * 60 * 1000);
                return created.toLocaleDateString('ru-RU');
            }
        },
        {
            title: 'Истекает',
            dataIndex: 'expiresAt',
            key: 'expiresAt',
            render: (expiresAt: string) => new Date(expiresAt).toLocaleDateString('ru-RU')
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: InviteToken) => (
                <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => handleRevokeToken(record.token)}
                    disabled={record.isUsed}
                >
                    Отозвать
                </Button>
            )
        }
    ];

    const activeTokens = inviteTokens.filter(token => !token.isUsed && new Date(token.expiresAt) > new Date());
    const usedTokens = inviteTokens.filter(token => token.isUsed);
    const expiredTokens = inviteTokens.filter(token => !token.isUsed && new Date(token.expiresAt) <= new Date());

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="mb-8">
                <Title level={2}>Управление пользователями</Title>
                <Text type="secondary">
                    Приглашение пользователей из системы 1С в админ-панель
                </Text>
            </div>

            {/* Статистика */}
            <Row gutter={16} className="mb-6">
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Пользователи из 1С"
                            value={usersFrom1C.length}
                            prefix={<UserOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Активные приглашения"
                            value={activeTokens.length}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Использованные"
                            value={usedTokens.length}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Истекшие"
                            value={expiredTokens.length}
                            prefix={<ExclamationCircleOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Пользователи из 1С */}
            <Card
                title="Пользователи из системы 1С"
                className="mb-6"
                extra={
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadData}
                            loading={loading}
                        >
                            Обновить
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                console.log('Invite users button clicked');
                                setInviteModalVisible(true);
                            }}
                        >
                            Пригласить пользователей
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={usersColumns}
                    dataSource={usersFrom1C}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} из ${total} пользователей`
                    }}
                    rowSelection={{
                        type: 'checkbox',
                        onChange: (selectedRowKeys, selectedRows) => {
                            setSelectedUsers(selectedRows);
                        }
                    }}
                />
            </Card>

            {/* Токены приглашений */}
            <Card title="История приглашений">
                <Table
                    columns={tokensColumns}
                    dataSource={inviteTokens}
                    rowKey="token"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} из ${total} приглашений`
                    }}
                />
            </Card>

            {/* Модальное окно приглашения */}
            <InviteUsersModal
                visible={inviteModalVisible}
                users={selectedUsers}
                onCancel={() => {
                    setInviteModalVisible(false);
                    setSelectedUsers([]);
                }}
                onSuccess={handleInviteSuccess}
            />
        </div>
    );
};
